// src/utils/recurringMath.js
// Occurrence maths for recurring transactions, shared by the Budget tab's
// Scheduled list and the Upcoming Bills widget. One function, one answer.

// Every occurrence of every recurring item inside [windowStart, windowEnd],
// each marked isAdded if a matching transaction already exists.
export const enumerateOccurrences = ({
  recurring = [],
  windowStart,
  windowEnd,
  transactions = [],
  viewMonth = null,
} = {}) => {
  const scheduledItems = [];

  (recurring || []).forEach(rec => {
    if (!rec.start_date || !rec.amount) return;

    const startDate = new Date(rec.start_date + 'T00:00:00');
    if (startDate > windowEnd) return;

    let intervalDays;
    if (rec.frequency === 'weekly') intervalDays = 7;
    else if (rec.frequency === 'biweekly') intervalDays = 14;
    else if (rec.frequency === 'every_n_weeks') intervalDays = (rec.every_n_weeks || 2) * 7;
    else intervalDays = null;

    if (intervalDays) {
      const daysSinceStart = Math.floor((windowStart - startDate) / (1000 * 60 * 60 * 24));
      let occurrence = daysSinceStart > 0 ? Math.floor(daysSinceStart / intervalDays) : 0;

      while (true) {
        const occurrenceDate = new Date(startDate);
        occurrenceDate.setDate(startDate.getDate() + (occurrence * intervalDays));

        if (occurrenceDate > windowEnd) break;

        if (occurrenceDate >= windowStart && occurrenceDate <= windowEnd) {
          scheduledItems.push({ ...rec, scheduledDate: occurrenceDate, isAdded: false });
        }
        occurrence++;
        if (occurrence > 100) break;
      }
      // Count-based isAdded: mark earliest N occurrences as added
      const addedCount = (transactions || []).filter(t => t.is_recurring && t.recurring_id === rec.id).length;
      const recItems = scheduledItems.filter(s => s.id === rec.id && s.isAdded === false);
      recItems.sort((a, b) => a.scheduledDate - b.scheduledDate);
      for (let ci = 0; ci < Math.min(addedCount, recItems.length); ci++) {
        recItems[ci].isAdded = true;
      }
    } else {
      // Monthly: the occurrence lands on the start day, in the window's month
      const anchor = viewMonth || windowStart;
      const occurrenceDate = new Date(startDate);
      occurrenceDate.setFullYear(anchor.getFullYear());
      occurrenceDate.setMonth(anchor.getMonth());

      if (occurrenceDate >= windowStart && occurrenceDate <= windowEnd) {
        const isAdded = (transactions || []).some(t =>
          t.is_recurring && t.recurring_id === rec.id
        );
        scheduledItems.push({ ...rec, scheduledDate: occurrenceDate, isAdded });
      }
    }
  });

  return scheduledItems;
};

// ---- Bill colour -----------------------------------------------------
// Categories are whatever the user types, so there is no fixed map. The colour
// comes from the category name itself: the same name always gets the same
// colour, and if two visible rows collide the later one takes the next free
// slot so a list never repeats.
const BILL_TINTS = {
  // Purple (admin-only): one lavender family.
  purple: Array.from({ length: 4 }, () => ({ chip: '#EADFF8', chipText: '#5E35A8', ink: '#6E4FC6' })),
  pink: [
    { chip: '#F3CCD6', chipText: '#A32A5E', ink: '#C23A72' },
    { chip: '#D8CCEC', chipText: '#5B4489', ink: '#7A5FB0' },
    { chip: '#C4D6ED', chipText: '#2F5480', ink: '#3C6693' },
    { chip: '#CFE0C0', chipText: '#3E5A30', ink: '#4F6E3D' },
  ],
  warm: [
    { chip: '#F0D8C2', chipText: '#8A4A15', ink: '#B5651D' },
    { chip: '#E5D0C2', chipText: '#75422A', ink: '#9A5B3C' },
    { chip: '#E2D3BC', chipText: '#5C4F36', ink: '#7A6A4A' },
    { chip: '#DCE6C1', chipText: '#455C37', ink: '#5C7A4A' },
  ],
  blue: [
    { chip: '#D2DEF5', chipText: '#2B4196', ink: '#3A56C2' },
    { chip: '#D6D6ED', chipText: '#443B74', ink: '#5B4F93' },
    { chip: '#C9E4E6', chipText: '#235C61', ink: '#2F7A80' },
    { chip: '#D2CDEB', chipText: '#50458A', ink: '#6B5FB0' },
  ],
};

const hashIndex = (text, slots) => {
  const s = String(text || '');
  let h = 0;
  for (let i = 0; i < s.length; i++) {
    h = ((h << 5) - h + s.charCodeAt(i)) | 0;
  }
  return Math.abs(h) % slots;
};

// ---- Widget payload --------------------------------------------------
const monthKeyOf = (d) => `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}`;

export const buildBillsWidgetData = ({
  recurringTransactions = [],
  transactions = [],
  from = new Date(),
  days = 7,
  theme = 'pink',
} = {}) => {
  const windowStart = new Date(from.getFullYear(), from.getMonth(), from.getDate(), 0, 0, 0);
  const windowEnd = new Date(from.getFullYear(), from.getMonth(), from.getDate() + days, 23, 59, 59);

  // Bills are money going out: expenses and debt payments. Income and savings
  // transfers are deliberately left out.
  const outgoing = (recurringTransactions || []).filter(r => r.type !== 'income' && r.type !== 'savings');

  // Enumerate a WHOLE CALENDAR MONTH at a time — the same window the Scheduled
  // list uses — so "already posted" is judged against the month, and only then
  // narrow to the widget's window. Enumerating the short window directly is
  // wrong: the count-based isAdded rule marks the earliest N occurrences as
  // posted, so a payment posted on the 10th would cancel the one due on the
  // 24th simply because the 10th was outside the window.
  const months = [];
  const cursor = new Date(windowStart.getFullYear(), windowStart.getMonth(), 1);
  const lastMonth = new Date(windowEnd.getFullYear(), windowEnd.getMonth(), 1);
  while (cursor <= lastMonth) {
    months.push(new Date(cursor));
    cursor.setMonth(cursor.getMonth() + 1);
  }

  let occurrences = [];
  months.forEach(m => {
    const monthStart = new Date(m.getFullYear(), m.getMonth(), 1);
    const monthEnd = new Date(m.getFullYear(), m.getMonth() + 1, 0, 23, 59, 59);
    const key = monthKeyOf(m);
    const monthTransactions = (transactions || []).filter(t => (
      t.month_key ? t.month_key === key : (t.date && monthKeyOf(new Date(t.date)) === key)
    ));
    occurrences = occurrences.concat(enumerateOccurrences({
      recurring: outgoing,
      windowStart: monthStart,
      windowEnd: monthEnd,
      transactions: monthTransactions,
      viewMonth: m,
    }));
  });

  occurrences = occurrences
    .filter(o => !o.isAdded)
    .filter(o => o.scheduledDate >= windowStart && o.scheduledDate <= windowEnd)
    .sort((a, b) => a.scheduledDate - b.scheduledDate);

  const palette = BILL_TINTS[theme] || BILL_TINTS.pink;
  const used = new Set();
  const dayNames = ['SUN', 'MON', 'TUE', 'WED', 'THU', 'FRI', 'SAT'];

  const items = occurrences.map(o => {
    const label = o.category || o.description || 'Other';
    let slot = hashIndex(label, palette.length);
    // Nudge to the next free slot so no two visible rows share a colour
    for (let i = 0; i < palette.length && used.has(slot); i++) {
      slot = (slot + 1) % palette.length;
    }
    used.add(slot);
    const tint = palette[slot];
    const d = o.scheduledDate;
    return {
      id: `${o.id}-${d.getTime()}`,
      name: o.description || o.category || 'Bill',
      amount: parseFloat(o.amount) || 0,
      day: dayNames[d.getDay()],
      dayNumber: d.getDate(),
      chip: tint.chip,
      chipText: tint.chipText,
      ink: tint.ink,
    };
  });

  return {
    total: items.reduce((sum, i) => sum + i.amount, 0),
    count: items.length,
    days,
    items: items.slice(0, 4),
  };
};
